import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEvent } from "../context/EventProvider";
import { sameSession, SessionChangedError } from "../auth/session";
import { useSessionKey } from "../auth/useSessionKey";
import { logAction } from "../log/actionLog";
import { FieldRow, Remaining } from "../funnel/Champ";
import { allInvalid, type Complaint } from "../funnel/checks";
import { visibleFields } from "../funnel/conditions";
import {
  searchByIdentifier,
  detach,
  fetchMyCompanies,
  attach,
  resume,
  keepForEvent,
  type Company as Company,
} from "../funnel/companies";
import {
  COUNTRIES,
  OFFERED_COUNTRIES,
  indexDe,
  identifierRefusal,
  checkIndex,
  cleanIndex,
  capIndex,
  type Country,
} from "../funnel/companyIndex";
import {
  fetchAnswers,
  saveAnswers,
  valueOf,
  lockedOf,
} from "../funnel/forms";
import { useChosenForm, useWords } from "../funnel/useForm";
import { useFunnelNav } from "../funnel/useFunnelNav";
import { useParticipation } from "../funnel/useParticipation";
import { useCompany } from "../funnel/useSociete";
import { useMode } from "../panel/mode";
import { fallbackOf } from "../settings/catalog";
import { useFlagOr, useSettings, useT, useText } from "../settings/context";
import { Button } from "../ui/Button";
import { Confirm } from "../ui/Confirm";
import { Icon } from "../ui/Icon";
import { Prose } from "../ui/Prose";
import { InPlace } from "../panel/InPlace";

/**
 * L'ÉTAPE SOCIÉTÉ — pour QUI le participant vient.
 *
 * ⚠️ TROIS VUES, UN SEUL ÉCRAN, et c'est la forme de la V1 (`screens/companies/`),
 * reprise parce qu'elle répond à une contrainte du produit et non à un goût :
 *
 *   LA LISTE        ses sociétés — il peut en représenter plusieurs, et l'événement
 *                   n'en retient qu'une.
 *   L'IDENTIFIANT   avant de créer quoi que ce soit. Une société appartient au
 *                   DOMAINE : deux visiteurs du même salon venus de la même maison
 *                   doivent tomber sur la même fiche, sinon l'organisateur relit sa
 *                   liste d'exposants en double — et le serveur refuse de toute façon
 *                   un identifiant déjà pris.
 *   LA FICHE        le formulaire de société du back office, comme l'étape
 *                   informations sert celui du participant.
 *
 * ⚠️ ELLE ÉCRIT AU FUR ET À MESURE, pas à la confirmation. Rattacher quelqu'un à une
 * société est une écriture partagée — la fiche existe pour les autres participants dès
 * qu'elle est créée — et rien de tout cela ne tient dans l'état de navigation.
 *
 * ⚠️ CE QUE LA V1 FAIT ET QU'ON NE REFAIT PAS : elle écrit `UserEvents.employeeId`
 * depuis le navigateur, à partir d'une réponse de route qui est littéralement `{}` —
 * donc `undefined`. La route apix le pose elle-même dès qu'on lui donne l'événement.
 * Voir `funnel/companies.ts`.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback — EXCEPT the messages addressed to the ORGANISER (an empty catalog,
 * a step removed from the thread), French like the rest of the panel.
 */

const ICI = "/registration/company";

/** Une société sur laquelle l'écran travaille : celle qu'on modifie, ou aucune. */
type Fiche = {
  /** `undefined` : on la crée. La route accepte `companyId=0` pour ça. */
  company?: Company;
  identifier: string;
  country: Country;
};

/**
 * Le bandeau d'échec, dans les mots de ce qui a échoué. L'état porte soit l'une de ces
 * clés, soit une phrase déjà écrite par le serveur et traduite (le refus de
 * l'identifiant) — d'où le repli sur la valeur elle-même, plus bas.
 *
 * Hors du composant : une table de constantes recréée à chaque rendu ne sert à rien.
 */
/* Chaque cause porte sa clé de dictionnaire ; le français reste le repli. */
const FAILURES: Record<string, readonly [key: string, french: string]> = {
  search: [
    "COMPANY_SEARCH_FAILED",
    "La recherche n'a pas abouti. Réessayez, et prévenez l'organisateur si cela persiste.",
  ],
  attachment: [
    "COMPANY_ATTACHMENT_NOT_SAVED",
    "Le rattachement n'a pas pu être enregistré. Réessayez.",
  ],
  removal: [
    "COMPANY_REMOVAL_NOT_SAVED",
    "Le retrait n'a pas pu être enregistré. Réessayez.",
  ],
  write: ["COMPANY_NOT_SAVED", "La fiche n'a pas pu être enregistrée. Réessayez."],
  "no-form": [
    "COMPANY_NO_FORM_TO_CREATE",
    "Aucune société ne porte cet identifiant, et cette étape n'a pas de formulaire pour en créer une. Prévenez l'organisateur.",
  ],
};

/**
 * ⚠️ SERVI AUSSI COMME PAGE (`/company`) — voir `pages/stepPages.ts`. L'écran est le
 * même ; ce qui change est qu'il n'y a pas d'étape après, donc pas de bouton du bas :
 * choisir une carte la rattache aussitôt, et le dit (voir `pick`).
 */
export function Company({ asPage }: { asPage?: true }) {
  const t = useT();
  const { value } = useSettings();
  const event = useEvent();
  const client = useQueryClient();
  const { me } = useParticipation();
  const sessionKey = useSessionKey();

  const title = useText(
    "FO_MEETING_V2_COMPANY_TITLE",
    fallbackOf("FO_MEETING_V2_COMPANY_TITLE"),
  );
  const subtitle = useText(
    "FO_MEETING_V2_COMPANY_SUBTITLE",
    fallbackOf("FO_MEETING_V2_COMPANY_SUBTITLE"),
  );
  const html = useText("FO_MEETING_V2_COMPANY_HTML");
  const indexHtml = useText("FO_MEETING_V2_COMPANY_INDEX_HTML");
  const indexLabel = useText(
    "FO_MEETING_V2_COMPANY_INDEX_LABEL",
    fallbackOf("FO_MEETING_V2_COMPANY_INDEX_LABEL"),
  );
  const inter = useFlagOr("FO_MEETING_V2_COMPANY_INDEX_INTER");
  const sizeSet = value("FO_MEETING_V2_COMPANY_INDEX_SIZE");

  /* Toujours demandée ici : c'est l'écran de l'étape, l'organisateur peut l'ouvrir
     même après l'avoir retirée du parcours, et une liste vide s'y lirait comme une
     panne. */
  const { companies, attached, failure: listFailure } = useCompany(true);

  /** La vue courante. Le retour de chacune est la liste — jamais l'étape d'avant. */
  const [view, setView] = useState<"liste" | "identifiant" | "fiche">("liste");
  const [chosen, setChosen] = useState<number | null>(null);

  /**
   * ⚠️ CELLE DU SERVEUR D'ABORD, PUIS L'ÉVIDENCE. Le rattachement enregistré est ce
   * qui fait foi ; à défaut, une seule société ne laisse aucun choix à faire, et la
   * V1 la retient déjà d'office. Dérivé et non posé dans un effet : les sociétés
   * arrivent après le premier rendu.
   *
   * ⚠️ PAS D'ÉVIDENCE EN PAGE : le clic y enregistre, et aucun bouton ne vient après.
   * Une carte marquée d'office y aurait dit retenue une société qui ne l'est pas.
   */
  const selection =
    chosen ??
    attached?.id ??
    (!asPage && companies.length === 1 ? companies[0]!.id : null);

  /** La société dont on demande confirmation du retrait, le temps de la question. */
  const [toRemove, setToRemove] = useState<Company | null>(null);

  const { previous, next, go } = useFunnelNav(ICI);

  /* Le formulaire de la fiche, du back office — même lecture que l'étape informations
     et que le panneau, voir `funnel/useForm.ts`. */
  const form = useChosenForm("FO_MEETING_V2_COMPANY_FORM_ID");
  const words = useWords(form);
  const organiser = useMode() === "organiser";

  /* ── L'IDENTIFIANT ── */
  const [country, setCountry] = useState<Country>("FR");
  const [identifier, setIdentifier] = useState("");
  const [indexComplaint, setIndexComplaint] = useState<string | null>(null);
  /** La société trouvée sous cet identifiant : on propose de la rejoindre. */
  const [found, setFound] = useState<Company | null>(null);
  const [search, setSearch] = useState(false);

  /* ── LA FICHE ── */
  const [fiche, setFiche] = useState<Fiche | null>(null);

  /**
   * ⚠️ LE PAYS DE LA FICHE OUVERTE PASSE DEVANT CELUI DU CHAMP. On modifie une société
   * suisse depuis un écran resté sur « France » : l'intitulé rappelé au-dessus de la
   * fiche aurait annoncé un SIRET au-dessus d'un UID suisse.
   */
  const index = indexDe({
    inter,
    country: view === "fiche" && fiche ? fiche.country : country,
    label: indexLabel,
    taille: typeof sizeSet === "number" ? sizeSet : undefined,
  }, t);

  const [typed, setTyped] = useState<Record<string, unknown>>({});
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  /* A-t-on déjà essayé d'envoyer la fiche ? La phrase du bas ne parle qu'après : sur une
     fiche vierge, elle reprocherait de ne pas avoir rempli ce qu'on vient d'ouvrir. */
  const [tried, setTried] = useState(false);
  const [sending, setSending] = useState(false);
  /** Une écriture qui n'a pas abouti — dite, jamais avalée. */
  const [failure, setFailure] = useState<string | null>(null);
  /**
   * Servie en page : « c'est enregistré », puisqu'on ne va nulle part.
   *
   * ⚠️ LA CARTE NE LE PROUVE PAS : elle est marquée dès le clic, par `chosen`, avant que
   * l'écriture ne revienne (voir `pick`). Sans ce mot, l'écran était le même avant et
   * après l'enregistrement.
   * ⚠️ ET IL S'EFFACE dès que l'écran change de sujet (autre carte, fiche, retrait) :
   * resté affiché, il dirait enregistré un choix qui ne l'est pas encore.
   */
  const [saved, setSaved] = useState(false);

  /**
   * Ce que la fiche porte déjà. Une société qu'on CRÉE n'a rien à relire : la requête
   * ne part pas, et le formulaire s'ouvre vide.
   */
  const stored = useQuery({
    queryKey: ["company-answers", event?.domainId, fiche?.company?.id],
    queryFn: ({ signal }) =>
      fetchAnswers(event!.domainId, { companyId: fiche!.company!.id }, signal),
    enabled: !!event && typeof fiche?.company?.id === "number",
    staleTime: Infinity,
    retry: false,
  });

  const fields = form?.fields ?? [];
  const answers = stored.data ?? {};
  const values = Object.fromEntries(
    fields.map((f) => [
      f.id,
      f.id in typed ? typed[f.id] : valueOf(answers, f),
    ]),
  );

  /**
   * ⚠️ L'IDENTIFIANT EST SAISI À L'ÉCRAN D'AVANT, PAS DANS LE FORMULAIRE — et il faut
   * quand même qu'il arrive en base, sinon la fiche créée n'a rien qui la distingue.
   * Deux cas, un seul résultat :
   *
   *   — le formulaire pose la question lui-même (`C.siren`) : on l'y verse et on
   *     l'y verrouille, plutôt que d'afficher deux fois le même nombre ;
   *   — il ne la pose pas : une ligne à part le rappelle, et `socle` l'écrit.
   */
  const indexField = fields.find(
    (f) => f.model === "C" && !f.extended && f.key === "siren",
  );
  if (indexField && fiche) values[indexField.id] = fiche.identifier;

  /**
   * CE QUI EST VERROUILLÉ — la règle commune (voir `verrouillesDe`), PLUS l'identifiant,
   * qui l'est toujours : il vient d'être saisi à l'écran d'avant, et le champ ne fait
   * que le rappeler.
   */
  const locked = new Set([
    ...(indexField ? [indexField.id] : []),
    ...lockedOf(fields, answers),
  ]);

  /* Ce qui empêche encore la fiche de partir, recalculé au rendu : la phrase du bas ne
     nomme que ce qui manque encore, et s'efface d'elle-même. Voir `Informations.tsx`, où
     la règle est écrite. */
  const invalid = allInvalid(fields, (f) => values[f.id], locked);

  const ready = !!event && typeof me?.id === "number";

  const forget = () => {
    setTyped({});
    setComplaints([]);
    setFailure(null);
  };

  /** Revenir à la liste avec la société qu'on vient de traiter, sélectionnée. */
  const backToList = (id?: number) => {
    if (typeof id === "number") setChosen(id);
    setView("liste");
    setFound(null);
    setFiche(null);
    forget();
  };

  /**
   * RATTACHER, ET LE FAIRE VALOIR POUR CET ÉVÉNEMENT.
   *
   * Le lien `Employees` d'abord, la relecture ensuite — c'est elle qui donne son
   * identifiant, la route de rattachement ne rendant rien — puis la participation.
   * La seconde écriture n'est pas un doublon de la première : voir
   * `retientPourLEvenement`, qui dit ce qui manquait sans elle.
   *
   * ⚠️ `fetchQuery` ET NON UNE INVALIDATION : on a besoin de la liste fraîche TOUT DE
   * SUITE, pour y lire le rattachement qu'on vient de créer. Elle atterrit dans le
   * même cache au passage, donc la liste à l'écran est à jour sans second aller-retour.
   */
  const lie = async (companyId: number, record?: "Création" | "Modification") => {
    // Lue avant l'écriture : la société retenue jusqu'ici.
    const changed = companyId !== attached?.id;
    // ⚠️ Session changée depuis le rendu (autre onglet) : ce qui est ici est à l'ancien compte, rien ne part.
    if (!sameSession(sessionKey)) throw new SessionChangedError();
    await attach(event!.domainId, companyId, me!.id, event!.eventId);

    const fresh = await client.fetchQuery({
      queryKey: ["companies", event!.domainId, me!.id],
      queryFn: ({ signal }) =>
        fetchMyCompanies(event!.domainId, me!.id, signal),
      staleTime: 0,
    });

    const employeeId = fresh.find((c) => c.id === companyId)?.employeeId;
    /* ⚠️ PAS D'EMPLOYÉ, PAS DE RATTACHEMENT : continuer en silence laissait la société
       « retenue » à l'écran et `UserEvents.employeeId` vide (badge sans société). */
    if (typeof employeeId !== "number") {
      throw new Error("EMPLOYEE_MISSING");
    }
    await keepForEvent(event!.domainId, event!.eventId, me!.id, employeeId);
    // Le fil d'Ariane coche l'étape sur ce que porte la participation.
    await client.invalidateQueries({ queryKey: ["participation"] });
    /**
     * ⚠️ ET LES CONFÉRENCES, dont les verrous DÉPENDENT DE LA SOCIÉTÉ. Le serveur refuse
     * une conférence où un collaborateur de la même maison est déjà inscrit
     * (`collegueInscrit`) : changer de société change donc ce qui est ouvert. Revenir
     * en arrière pour se rattacher ailleurs, puis repasser aux conférences, servait des
     * verrous calculés pour la société précédente — une place annoncée libre que le
     * serveur refuse, ou l'inverse.
     */
    await client.invalidateQueries({ queryKey: ["conferences"] });
    /* Le journal de la fiche : une fiche créée ou modifiée, ou une autre société choisie.
       Repasser par l'étape sur la même société n'est pas un changement. */
    if (record || changed)
      logAction(event, "companyUpdate", { companyId, label: record ?? "Modification" });
  };

  const searchIdentifier = async (e: React.FormEvent) => {
    e.preventDefault();
    if (search || !event) return;
    setFailure(null);
    const mistake = checkIndex(identifier, index, t);
    setIndexComplaint(mistake);
    if (mistake) return;

    const own = cleanIndex(identifier, index);
    setSearch(true);
    try {
      const found = await searchByIdentifier(event.domainId, own);
      /* Déjà l'une des siennes : rien à rejoindre ni à créer, on la retient et on
         revient. Sans ce cas, saisir l'identifiant d'une société déjà dans sa liste
         proposait de la « rejoindre » une seconde fois. */
      const theirs = found.find((c) => companies.some((s) => s.id === c.id));
      if (theirs) return backToList(theirs.id);

      if (found[0]) return setFound(found[0]);
      // Personne ne la porte : on la crée, si tant est qu'il y ait de quoi la remplir.
      if (!form) return setFailure("no-form");
      setFiche({ identifier: own, country });
      forget();
      setView("fiche");
    } catch {
      setFailure("search");
    } finally {
      setSearch(false);
    }
  };

  const join = async (company: Company) => {
    if (!ready || sending) return;
    setSending(true);
    setFailure(null);
    try {
      await lie(company.id);
      backToList(company.id);
      if (asPage) setSaved(true);
    } catch {
      setFailure("attachment");
    } finally {
      setSending(false);
    }
  };

  /**
   * EN PAGE, CHOISIR UNE CARTE L'ENREGISTRE. Tout le reste de l'écran écrit au fur et à
   * mesure (rattacher, créer, retirer) : un « Enregistrer » qui ne servait qu'à ce
   * geste-là laissait croire que rien n'était écrit avant lui (#500).
   *
   * ⚠️ EN CAS D'ÉCHEC, LA CARTE REVIENT À CE QUE DIT LE SERVEUR : marquée dès le clic,
   * elle resterait sinon sur une société qui n'est pas retenue.
   */
  const pick = async (company: Company) => {
    setSaved(false);
    if (!asPage) return setChosen(company.id);
    if (!ready || sending || company.id === attached?.id) return;
    setChosen(company.id);
    setSending(true);
    setFailure(null);
    try {
      await lie(company.id);
      setSaved(true);
    } catch {
      setChosen(null);
      setFailure("attachment");
    } finally {
      setSending(false);
    }
  };

  /**
   * ⚠️ LA SOCIÉTÉ RETENUE POUR L'INSCRIPTION NE SE RETIRE PAS (#500). L'étape exige une
   * société à l'aller, et rien ne la redemande après : retirée, l'inscription restait
   * affichée complète sans elle. On en choisit une autre d'abord. C'est celle du
   * SERVEUR qui compte, pas la carte marquée : dans le tunnel, un choix que
   * « Continuer » n'a pas encore enregistré ne libère pas la précédente.
   */
  const removable = (company: Company) => company.id !== attached?.id;

  const remove = async (company: Company) => {
    if (!ready || sending) return;
    // ⚠️ Session changée depuis le rendu (autre onglet) : ce qui est ici est à l'ancien compte, rien ne part.
    if (!sameSession(sessionKey)) return;
    if (!removable(company)) return setToRemove(null);
    setSending(true);
    setFailure(null);
    setSaved(false);
    try {
      await detach(event!.domainId, company.id, me!.id);
      await Promise.all([
        client.invalidateQueries({ queryKey: ["companies"] }),
        client.invalidateQueries({ queryKey: ["participation"] }),
        // Même raison qu'au rattachement : les verrous des conférences se calculent
        // sur la société.
        client.invalidateQueries({ queryKey: ["conferences"] }),
      ]);
      if (chosen === company.id) setChosen(null);
    } catch {
      setFailure("removal");
    } finally {
      setSending(false);
      // La question reste ouverte pendant l'envoi, son bouton tournant : voir `busy`.
      setToRemove(null);
    }
  };

  const saveRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending || !ready || !fiche || !form) return;
    setComplaints([]);
    setFailure(null);
    // À partir d'ici, la phrase du bas a le droit de parler : on a essayé.
    setTried(true);

    /* Ce qui ne peut pas exister est arrêté avant le voyage — voir `checks.ts`. */
    const [first] = invalid;
    if (first) {
      setComplaints(invalid.map((i) => i.complaint));
      document.getElementById(`f-${first.complaint.id}`)?.focus();
      return;
    }

    // ⚠️ Session changée depuis le rendu (autre onglet) : la fiche saisie ici est à l'ancien compte.
    if (!sameSession(sessionKey)) return;
    setSending(true);
    try {
      const id = await saveAnswers({
        domainId: event!.domainId,
        // ⚠️ `0` VEUT DIRE « CRÉE-LA » — voir `Cible` dans `funnel/forms.ts`.
        target: { companyId: fiche.company?.id ?? 0 },
        fields,
        values,
        current: answers,
        locked,
        /* L'identifiant, et le pays quand il a été demandé. La V1 n'écrit que le
           premier : son pays est saisi puis perdu, donc la recherche suivante ne
           retrouve la fiche que parce que les fiches sans pays sont acceptées
           partout. Ici il est écrit, ce qui rend le mode international utile. */
        base: {
          C: {
            siren: fiche.identifier,
            ...(inter ? { country: fiche.country } : {}),
          },
        },
      });

      const cree = id ?? fiche.company?.id;
      if (typeof cree !== "number") throw new Error("Identifiant non rendu");
      await client.invalidateQueries({
        queryKey: ["company-answers", event!.domainId, cree],
        refetchType: "none",
      });

      /**
       * ⚠️ LA FICHE EST ENREGISTRÉE À PARTIR D'ICI, et le rattachement est une AUTRE
       * écriture. S'il échoue, dire « la fiche n'a pas pu être enregistrée » enverrait
       * quelqu'un la ressaisir — et il en créerait une seconde, sous un identifiant
       * que le serveur refuse. On revient donc à la liste, société en évidence, et
       * c'est « Continuer » qui retentera le rattachement.
       */
      let manque = false;
      try {
        await lie(cree, fiche.company ? "Modification" : "Création");
      } catch {
        manque = true;
      }
      backToList(cree);
      if (manque) setFailure("attachment");
      else if (asPage) setSaved(true);
    } catch (error) {
      setFailure(identifierRefusal(error, t) ?? "write");
    } finally {
      setSending(false);
    }
  };

  /** Dans le tunnel seulement : en page, le clic sur la carte a déjà enregistré. */
  const continuer = async () => {
    // Le bouton est déjà désactivé dans ces cas : la garde est là pour qu'un chemin
    // qu'on n'a pas vu ne puisse pas avancer SANS avoir rattaché.
    if (!next || sending || !ready || selection === null) return;
    setSending(true);
    setFailure(null);
    try {
      await lie(selection);
      go(next.path);
    } catch {
      setFailure("attachment");
    } finally {
      setSending(false);
    }
  };

  const said = failure ? FAILURES[failure] : undefined;
  const failureMessage = said ? t(said[0], said[1]) : (failure ?? null);

  return (
    <section className="page">
      <div className="ttl" data-cfg="companyTitle">
        <InPlace settingKey="FO_MEETING_V2_COMPANY_TITLE" text={title} as="h2" />
        <InPlace settingKey="FO_MEETING_V2_COMPANY_SUBTITLE" text={subtitle} as="p" />
      </div>

      {view === "liste" && html.trim() !== "" && (
        <Prose
          html={html}
          className="se-prose"
          data-cfg="companyTitle"
          />
      )}

      {/* ⚠️ DIT À L'ORGANISATEUR, parce que lui seul peut le corriger. Sans formulaire
          l'étape rattache encore à une société existante — elle ne peut simplement
          plus en créer, ce que la phrase dit plutôt que de le laisser découvrir au
          bout de l'écran d'identifiant. ⚠️ À LUI SEUL : le participant n'a pas de
          formulaire à choisir. Lui l'apprend au moment où ça le concerne, sur l'écran
          d'identifiant (`COMPANY_NO_FORM_TO_CREATE`, qui lui dit de prévenir
          l'organisateur). */}
      <div className="alert" data-cfg="companyForm" hidden={!!form || !organiser}>
        <span className="alert__d" aria-hidden="true">
          <i />
        </span>
        <span>
          <b>Aucun formulaire de fiche</b>
          <small>
            Choisissez celui que cette étape doit servir : sans lui, personne ne
            peut créer la société qui manque.
          </small>
        </span>
      </div>

      {failureMessage && (
        <p className="loginerr" role="alert">
          {failureMessage}
        </p>
      )}

      {/* ═══ LA LISTE ═══ */}
      {view === "liste" && (
        <>
          {listFailure && (
            <p className="loginerr" role="alert">
              {t("COMPANIES_NOT_LOADED", "Vos sociétés n'ont pas pu être lues. Rechargez la page.")}
            </p>
          )}

          {companies.length === 0 ? (
            <p className="empty">
              {t("NO_COMPANY_YET", 'Aucune société ne vous est rattachée pour le moment.')}
            </p>
          ) : (
            <ul className="opts">
              {companies.map((company) => (
                <li key={company.id} className="opts__row">
                  <div
                    className={`opt${selection === company.id ? " on" : ""}`}
                  >
                    {/**
                     * ═══ LA CARTE ENTIÈRE CHOISIT, ET PORTE SES DEUX GESTES ═══
                     *
                     * ⚠️ LA CARTE N'EST PLUS UN BOUTON, ET IL A FALLU ÇA. Un bouton
                     * dans un bouton n'est pas du HTML valide — le navigateur défait
                     * l'imbrication et la carte cesse d'être cliquable. C'est
                     * exactement pourquoi « Modifier » et « Retirer » vivaient sur une
                     * ligne SOUS la carte. Le choix devient donc un calque étendu à
                     * toute la carte, et les deux gestes passent au-dessus de lui.
                     *
                     * ⚠️ IL LUI FAUT UN NOM À LUI. Vide, ce bouton n'annonce rien à une
                     * synthèse vocale : le texte qu'il recouvre n'est pas le sien. D'où
                     * le libellé explicite, qui dit AUSSI ce que le clic fait.
                     */}
                    <button
                      type="button"
                      className="opt__pick"
                      aria-pressed={selection === company.id}
                      aria-label={t("SELECT_COMPANY_N", "Choisir $1", [company.name])}
                      onClick={() => void pick(company)}
                    />
                    {/* ⚠️ `company`, PAS « societe » : la table des icônes ne connaît
                        pas ce nom-là, et elle ne dit jamais non — elle rendait le
                        REPÈRE DE CARTE de son repli à côté de chaque nom d'entreprise
                        (voir `ui/Icon.test.tsx`, qui l'a trouvé). */}
                    <span className="opt__i" aria-hidden="true">
                      <Icon name="company" />
                    </span>
                    <span className="opt__t">
                      <b>{company.name}</b>
                      {resume(company) && <small>{resume(company)}</small>}
                      {/* Ce qui dit pourquoi « Retirer » manque sur cette carte. */}
                      {!removable(company) && (
                        <small>
                          {t(
                            "COMPANY_OF_YOUR_REGISTRATION",
                            "Retenue pour votre inscription",
                          )}
                        </small>
                      )}
                    </span>
                    <div className="opts__acts">
                      <button
                        type="button"
                        className="lnk"
                        disabled={!form || sending}
                        onClick={() => {
                          setSaved(false);
                          setFiche({
                            company,
                            identifier: company.siren,
                            country: (company.country as Country) || "FR",
                          });
                          forget();
                          setView("fiche");
                        }}
                      >
                        {t("EDIT", "Modifier")}
                      </button>
                      {removable(company) && (
                        <button
                          type="button"
                          className="lnk"
                          disabled={!ready || sending}
                          onClick={() => {
                            setSaved(false);
                            setToRemove(company);
                          }}
                        >
                          {t("REMOVE", "Retirer")}
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* ⚠️ AU-DESSUS DE « CONTINUER », PAS À CÔTÉ. Elle n'agit pas sur l'étape
              mais sur la LISTE, et rangée dans la même rangée que Retour/Continuer
              elle se lisait comme une troisième façon d'avancer. La V1 la pose aussi
              au-dessus de son tableau. */}
          <div className="opts__add">
            <Button
              type="button"
              variant="ghost"
              data-cfg="companyIndex"
              onClick={() => {
                setIdentifier("");
                setIndexComplaint(null);
                setFound(null);
                setFailure(null);
                setSaved(false);
                setView("identifiant");
              }}
            >
              {t("ADD_A_COMPANY", "Ajouter une société")}
            </Button>
          </div>

          {asPage && saved && (
            <p className="f--wide step__ok" role="status">
              {t("YOUR_CHANGES_SAVED", 'Vos modifications sont enregistrées.')}
            </p>
          )}

          <div className="actions">
            {/* Même raison qu'aux informations : hors du tunnel, rien ne ramène. */}
            {asPage && (
              <Button type="button" variant="ghost" onClick={() => go("/")}>
                {t("BACK_TO_HOME", "Retour à l'accueil")}
              </Button>
            )}
            {!asPage && previous && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => go(previous.path)}
              >
                {t("BACK", "Retour")}
              </Button>
            )}
            {/* ⚠️ PAS D'« ENREGISTRER » EN PAGE (#500) : le clic sur la carte enregistre
                déjà, voir `pick`. */}
            {!asPage && (
              <Button
                type="button"
                loading={sending}
                /* Une étape société sans société ne dit rien à l'organisateur : c'est
                   la règle de la V1, dont le bouton est désactivé tant que rien n'est
                   retenu. */
                disabled={selection === null || !next}
                onClick={() => void continuer()}
              >
                {t("CONTINUE", "Continuer")}
              </Button>
            )}
          </div>
        </>
      )}

      {/* ═══ L'IDENTIFIANT ═══ */}
      {view === "identifiant" && (
        <>
          {indexHtml.trim() !== "" && (
            <Prose
              html={indexHtml}
              className="se-prose"
              data-cfg="companyIndex"
              />
          )}

          {found ? (
            <>
              <div className="alert" data-cfg="companyIndex">
                <span className="alert__d" aria-hidden="true">
                  <i />
                </span>
                <span>
                  <b>{t("COMPANY_ALREADY_EXISTS", 'Cette société existe déjà')}</b>
                  <small>
                    {t(
                      "COMPANY_JOIN_WARNING",
                      "$1 porte déjà cet identifiant. Si vous la rejoignez, toutes les personnes déjà rattachées à cette société seront averties de votre rattachement, avec votre nom et votre adresse email. Ne la rejoignez que si vous en faites réellement partie.",
                      [found.name],
                    )}
                  </small>
                </span>
              </div>
              <div className="actions">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setFound(null)}
                >
                  {t("START_OVER", "Recommencer")}
                </Button>
                <Button
                  type="button"
                  loading={sending}
                  disabled={!ready}
                  onClick={() => void join(found)}
                >
                  {t("JOIN_THIS_COMPANY", "Rejoindre cette société")}
                </Button>
              </div>
            </>
          ) : (
            <form className="form" onSubmit={searchIdentifier}>
              {/* Le pays d'abord : c'est lui qui décide du format attendu, donc de ce
                  que l'intitulé du champ en dessous annonce. */}
              {inter && (
                <div className="f f--moyen" data-cfg="companyIndex">
                  <label htmlFor="co-pays">{t("COMPANY_COUNTRY", 'Pays de la société')}</label>
                  <select
                    id="co-pays"
                    value={country}
                    onChange={(e) => {
                      setCountry(e.target.value as Country);
                      setIndexComplaint(null);
                    }}
                  >
                    {OFFERED_COUNTRIES.map((p) => (
                      <option key={p} value={p}>
                        {COUNTRIES[p].nameKey
                          ? t(COUNTRIES[p].nameKey, COUNTRIES[p].name)
                          : COUNTRIES[p].name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="f f--grand" data-cfg="companyIndex">
                <label htmlFor="co-index">
                  {index.label}
                  <i> *</i>
                </label>
                <input
                  id="co-index"
                  type="text"
                  autoComplete="off"
                  inputMode={inter ? "text" : "numeric"}
                  placeholder={index.example}
                  aria-invalid={indexComplaint ? true : undefined}
                  aria-describedby={indexComplaint ? "co-index-err" : undefined}
                  value={identifier}
                  onChange={(e) => {
                    setIdentifier(capIndex(e.target.value, index));
                    setIndexComplaint(null);
                  }}
                />
                {indexComplaint && (
                  <p className="f__err" id="co-index-err" role="alert">
                    {indexComplaint}
                  </p>
                )}
              </div>

              <div className="actions f--wide">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => backToList()}
                >
                  {t("BACK", "Retour")}
                </Button>
                <Button type="submit" loading={search}>
                  {t("SEARCH", "Rechercher")}
                </Button>
              </div>
            </form>
          )}
        </>
      )}

      {/* ═══ LA FICHE ═══ */}
      {view === "fiche" && fiche && form && words && (
        <form
          className="form"
          data-cfg="companyForm"
          /* Le reproche est celui de l'écran, pas la bulle du navigateur — voir
             `Informations.tsx`. */
          noValidate
          onSubmit={saveRecord}
        >
          {/* L'identifiant, rappelé quand le formulaire ne le pose pas lui-même : il
              vient d'être saisi, et une fiche dont on ne voit plus la clé n'est pas
              vérifiable avant d'être enregistrée. */}
          {!indexField && (
            <div className="f f--grand">
              <label htmlFor="co-index-fixe">
                {index.label}
                <small className="f__lock">{t("NOT_EDITABLE", 'Non modifiable')}</small>
              </label>
              <input
                id="co-index-fixe"
                type="text"
                readOnly
                value={fiche.identifier}
              />
            </div>
          )}

          {visibleFields(fields, (f) => values[f.id]).map((field) => (
            <FieldRow
              key={field.id}
              field={field}
              value={values[field.id]}
              locked={locked.has(field.id)}
              words={words}
              error={complaints.find((r) => r.id === field.id)?.message}
              onChange={(v) => {
                setTyped((s) => ({ ...s, [field.id]: v }));
                setComplaints((rs) => rs.filter((r) => r.id !== field.id));
              }}
            />
          ))}

          {/* Ce que le bouton ne dit pas : il reste cliquable, et le clic est le
              diagnostic. Voir `resteAFaire`. */}
          {tried && invalid.length > 0 && (
            <p className="f--wide hint" role="status">
              <Remaining invalid={invalid} words={words} />
            </p>
          )}

          <div className="actions f--wide">
            <Button type="button" variant="ghost" onClick={() => backToList()}>
              {t("BACK", "Retour")}
            </Button>
            <Button type="submit" loading={sending} disabled={!ready}>
              {fiche.company
                ? t("SAVE", "Enregistrer")
                : t("CREATE_THE_COMPANY", "Créer la société")}
            </Button>
          </div>
        </form>
      )}

      {/* ⚠️ UNE QUESTION AVANT LE RETRAIT (#500) : le clic écrit aussitôt, et rien à
          l'écran ne le laissait prévoir. */}
      <Confirm
        open={toRemove !== null}
        title={t("REMOVE_THIS_COMPANY_ASK", "Retirer cette société ?")}
        message={t(
          "REMOVE_THIS_COMPANY_WARNING",
          "$1 ne figurera plus parmi vos sociétés. Sa fiche, elle, est conservée.",
          [toRemove?.name ?? ""],
        )}
        confirmLabel={t("REMOVE", "Retirer")}
        onConfirm={() => toRemove && void remove(toRemove)}
        onCancel={() => setToRemove(null)}
        busy={sending}
        /* Un retrait par mégarde se rattrape mal : c'est le bouton qui NE fait rien
           qui doit être le plus facile à trouver. */
        emphasis="cancel"
      />
    </section>
  );
}
